/**
 * Supabase browser client.
 *
 * The web/extension client only ever holds the ANON key — NEVER the service
 * role key. The service role lives exclusively in the server (VPS) env. The
 * anon key + per-user RLS (including the can_reveal_ai self-score gate) are what
 * keep this safe. All privileged work (signed-URL minting, AI reveal, manager
 * overrides) goes through the server API via lib/api.ts, not direct table
 * writes from here.
 *
 * Session persistence uses the platform-appropriate storage adapter:
 *   - extension → chrome.storage.local (async)
 *   - web       → window.localStorage
 */
import { createClient, type SupabaseClient } from "@supabase/supabase-js";
import { getStorageAdapter } from "@/platform";

const SUPABASE_URL = import.meta.env.VITE_SUPABASE_URL as string | undefined;
const SUPABASE_ANON_KEY = import.meta.env.VITE_SUPABASE_ANON_KEY as
  | string
  | undefined;

if (!SUPABASE_URL || !SUPABASE_ANON_KEY) {
  // Fail loud at boot rather than producing confusing 401s later.
  throw new Error(
    "Missing VITE_SUPABASE_URL or VITE_SUPABASE_ANON_KEY. Check web/.env.",
  );
}

/**
 * GoTrue accepts a custom storage object with the async getItem/setItem/
 * removeItem signature. Our adapter matches that, so the same client config
 * works in both shells.
 */
const storage = getStorageAdapter();

export const supabase: SupabaseClient = createClient(
  SUPABASE_URL,
  SUPABASE_ANON_KEY,
  {
    auth: {
      // Custom async storage (chrome.storage.local in the extension).
      storage: storage as unknown as never,
      persistSession: true,
      autoRefreshToken: true,
      // We are not handling OAuth redirects in v1 (email/password only).
      detectSessionInUrl: false,
      storageKey: "tkcc.auth",
    },
  },
);

/** Convenience: current access token (or null) for the API fetch wrapper. */
export async function getAccessToken(): Promise<string | null> {
  const { data } = await supabase.auth.getSession();
  return data.session?.access_token ?? null;
}
